<template>
  <div class="dashboard-container">
    <v-overlay :value="loading" class="align-center justify-center">
      <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-snackbar v-model="error.show" color="error" top>
      <div class="d-flex align-center">
        <v-icon left>mdi-alert-circle</v-icon>
        <div>
          <p class="py-0 my-0 font-weight-black">{{ error.title }}</p>
          {{ error.message }}
        </div>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="error.show = false">Fechar</v-btn>
      </template>
    </v-snackbar>

    <!-- Header -->
    <div class="dashboard-header mb-6" v-if="!loading">
      <div class="d-flex justify-space-between align-center flex-wrap">
        <div>
        </div>
        <v-btn color="grey" dark class="mb-2 ml-2" @click="atualizar()">
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <v-container class="pa-0" v-if="!loading" fluid>
      <!-- Alertas críticos -->
      <v-expand-transition>
        <v-alert v-if="dashboard.criticas.length" type="error" prominent border="left" class="mb-6" elevation="2">
          <div class="d-flex align-center">
            <v-icon large left>mdi-alert-octagon</v-icon>
            <div>
              <div class="text-h6 font-weight-bold">{{ dashboard.criticas.length }} críticas encontradas</div>
              <div class="text-body-2">Atenção necessária para os itens abaixo</div>
            </div>
          </div>
          <v-divider class="my-3" dark></v-divider>
          <v-chip-group column>
            <v-chip v-for="(item, idx) in dashboard.criticas.slice(0, 10)" :key="idx" color="white" text-color="error"
              small class="ma-1">
              <v-icon left small>mdi-alert-circle</v-icon>
              {{ item.critica }}
            </v-chip>
          </v-chip-group>
          <div v-if="dashboard.criticas.length > 10" class="mt-2 text-caption">
            + {{ dashboard.criticas.length - 10 }} outras críticas...
          </div>
        </v-alert>
      </v-expand-transition>

      <!-- KPI Cards -->
      <v-row class="mb-2">
        <v-col cols="12" sm="6" lg="3">
          <v-card class="kpi-card kpi-visitas" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex justify-space-between align-start">
                <div>
                  <p class="text-overline mb-1 white--text" style="opacity: 0.8">Visitas (30 dias)</p>
                  <p class="text-h4 font-weight-bold white--text mb-0">
                    {{ dashboard.totalizadores.visitas | formatNumber }}
                  </p>
                </div>
                <v-avatar color="rgba(255,255,255,0.2)" size="56">
                  <v-icon color="white" size="30">mdi-eye</v-icon>
                </v-avatar>
              </div>
              <div class="mt-3">
                <v-icon small color="white" style="opacity: 0.8">mdi-trending-up</v-icon>
                <span class="text-caption white--text" style="opacity: 0.8">Últimos 30 dias</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" lg="3">
          <v-card class="kpi-card kpi-faturamento" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex justify-space-between align-start">
                <div>
                  <p class="text-overline mb-1 white--text" style="opacity: 0.8">Faturamento (30 dias)</p>
                  <p class="text-h5 font-weight-bold white--text mb-0">
                    {{ formatCurrency(dashboard.totalizadores.faturamento) }}
                  </p>
                </div>
                <v-avatar color="rgba(255,255,255,0.2)" size="56">
                  <v-icon color="white" size="30">mdi-currency-brl</v-icon>
                </v-avatar>
              </div>
              <div class="mt-3">
                <v-icon small color="white" style="opacity: 0.8">mdi-chart-line</v-icon>
                <span class="text-caption white--text" style="opacity: 0.8">Receita total</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" lg="3">
          <v-card class="kpi-card kpi-usuarios" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex justify-space-between align-start">
                <div>
                  <p class="text-overline mb-1 white--text" style="opacity: 0.8">Usuários</p>
                  <p class="text-h4 font-weight-bold white--text mb-0">
                    {{ dashboard.totalizadores.usuarios | formatNumber }}
                  </p>
                </div>
                <v-avatar color="rgba(255,255,255,0.2)" size="56">
                  <v-icon color="white" size="30">mdi-account-group</v-icon>
                </v-avatar>
              </div>
              <div class="mt-3">
                <v-icon small color="white" style="opacity: 0.8">mdi-account-plus</v-icon>
                <span class="text-caption white--text" style="opacity: 0.8">Total cadastrados</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" lg="3">
          <v-card class="kpi-card kpi-pedidos" elevation="4">
            <v-card-text class="pa-4">
              <div class="d-flex justify-space-between align-start">
                <div>
                  <p class="text-overline mb-1 white--text" style="opacity: 0.8">Pedidos</p>
                  <p class="text-h4 font-weight-bold white--text mb-0">
                    {{ dashboard.totalizadores.pedidos | formatNumber }}
                  </p>
                </div>
                <v-avatar color="rgba(255,255,255,0.2)" size="56">
                  <v-icon color="white" size="30">mdi-cart</v-icon>
                </v-avatar>
              </div>
              <div class="mt-3">
                <v-icon small color="white" style="opacity: 0.8">mdi-package-variant</v-icon>
                <span class="text-caption white--text" style="opacity: 0.8">Total realizados</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Estatísticas - cards pequenos -->
      <v-row class="mb-6">
        <v-col cols="6" sm="4" md="2">
          <v-card class="stat-mini-card" elevation="2">
            <v-card-text class="pa-3 text-center">
              <v-icon color="green" size="24">mdi-ticket-percent</v-icon>
              <p class="text-h6 font-weight-bold mb-0 mt-1">{{ formatCurrency(dashboard.infosRelevantes.ticketMedioPedido)
              }}</p>
              <p class="text-caption grey--text mb-0">Ticket médio</p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card class="stat-mini-card" elevation="2">
            <v-card-text class="pa-3 text-center">
              <v-icon color="amber" size="24">mdi-star</v-icon>
              <p class="text-h6 font-weight-bold mb-0 mt-1">{{ dashboard.infosRelevantes.avaliacoes.media }}</p>
              <p class="text-caption grey--text mb-0">Média de avaliações</p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card class="stat-mini-card" elevation="2">
            <v-card-text class="pa-3 text-center">
              <v-icon color="blue" size="24">mdi-repeat</v-icon>
              <p class="text-h6 font-weight-bold mb-0 mt-1">{{ dashboard.infosRelevantes.recorrenciaPedidos }}x</p>
              <p class="text-caption grey--text mb-0">Recorrência</p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card class="stat-mini-card" elevation="2">
            <v-card-text class="pa-3 text-center">
              <v-icon color="teal" size="24">mdi-store</v-icon>
              <p class="text-h6 font-weight-bold mb-0 mt-1">{{ dashboard.lojas.total | formatNumber }}</p>
              <p class="text-caption grey--text mb-0">Estabelecimentos</p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card class="stat-mini-card" elevation="2">
            <v-card-text class="pa-3 text-center">
              <v-icon color="green darken-2" size="24">mdi-store-check</v-icon>
              <p class="text-h6 font-weight-bold mb-0 mt-1">{{ dashboard.lojas.abertas | formatNumber }}</p>
              <p class="text-caption grey--text mb-0">Abertos</p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4" md="2">
          <v-card class="stat-mini-card" elevation="2">
            <v-card-text class="pa-3 text-center">
              <v-icon color="red" size="24">mdi-store-remove</v-icon>
              <p class="text-h6 font-weight-bold mb-0 mt-1">{{ dashboard.lojas.fechadas | formatNumber }}</p>
              <p class="text-caption grey--text mb-0">Fechados</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Gráficos principais -->
      <v-row class="mb-6">
        <!-- Gráfico status lojas -->
        <v-col cols="12" md="4">
          <v-card elevation="3" class="fill-height">
            <v-card-title class="pb-0">
              <v-icon left color="primary">mdi-chart-donut</v-icon>
              Status dos estabelecimentos
            </v-card-title>
            <v-card-text>
              <apexchart type="donut" height="280" :options="chartLojasOptions" :series="chartLojasSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Gráfico evolução NFC-e -->
        <v-col cols="12" md="8">
          <v-card elevation="3" class="fill-height">
            <v-card-title class="pb-0">
              <v-icon left color="primary">mdi-chart-line</v-icon>
              Evolução fiscal (últimos 3 meses)
            </v-card-title>
            <v-card-text>
              <apexchart type="area" height="280" :options="chartFiscalOptions" :series="chartFiscalSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Gráficos de ranking -->
      <v-row class="mb-6">
        <!-- Top Faturamento -->
        <v-col cols="12" md="6">
          <v-card elevation="3">
            <v-card-title class="pb-0">
              <v-icon left color="success">mdi-podium-gold</v-icon>
              Top 10 - Maior faturamento
            </v-card-title>
            <v-card-text>
              <apexchart type="bar" height="350" :options="chartTopFaturamentoOptions"
                :series="chartTopFaturamentoSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Top Vendas -->
        <v-col cols="12" md="6">
          <v-card elevation="3">
            <v-card-title class="pb-0">
              <v-icon left color="info">mdi-cart-arrow-up</v-icon>
              Top 10 - Mais vendas
            </v-card-title>
            <v-card-text>
              <apexchart type="bar" height="350" :options="chartTopVendasOptions" :series="chartTopVendasSeries">
              </apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Menor faturamento (lojas ativas) -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="pb-0">
              <v-icon left color="warning">mdi-alert-circle</v-icon>
              Top 10 - Menor faturamento (lojas ativas)
            </v-card-title>
            <v-card-subtitle class="mt-1">
              Apenas estabelecimentos ativos com menor faturamento nos últimos 30 dias
            </v-card-subtitle>
            <v-card-text>
              <apexchart type="bar" height="350" :options="chartMenorFaturamentoOptions"
                :series="chartMenorFaturamentoSeries">
              </apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Serviços e módulos -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="pb-0">
              <v-icon left color="purple">mdi-apps</v-icon>
              Utilização de serviços ({{ mesAtual }})
            </v-card-title>
            <v-card-text>
              <apexchart type="bar" height="300" :options="chartServicosOptions" :series="chartServicosSeries">
              </apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Comparativo Mensal -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title>
              <v-icon left color="orange">mdi-chart-timeline-variant</v-icon>
              Comparativo mensal - Serviços
            </v-card-title>
            <v-card-text>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Serviço</th>
                      <th class="text-center">{{ getMesNome(0) }}</th>
                      <th class="text-center">{{ getMesNome(-1) }}</th>
                      <th class="text-center">{{ getMesNome(-2) }}</th>
                      <th class="text-center">Tendência</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="servico in servicosComparativo" :key="servico.nome">
                      <td>
                        <v-icon small :color="servico.cor" class="mr-2">{{ servico.icone }}</v-icon>
                        {{ servico.nome }}
                      </td>
                      <td class="text-center font-weight-bold">{{ servico.mes1 | formatNumber }}</td>
                      <td class="text-center">{{ servico.mes2 | formatNumber }}</td>
                      <td class="text-center">{{ servico.mes3 | formatNumber }}</td>
                      <td class="text-center">
                        <v-chip :color="getTendenciaColor(servico.tendencia)" small dark>
                          <v-icon small left>{{ getTendenciaIcon(servico.tendencia) }}</v-icon>
                          {{ getTendenciaLabel(servico.tendencia) }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Cards situação -->
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <v-card class="situation-card" :class="{ 'situation-warning': dashboard.lojas.atrasados > 0 }" elevation="2">
            <v-card-text class="d-flex align-center pa-4">
              <v-avatar :color="dashboard.lojas.atrasados > 0 ? 'warning' : 'grey lighten-2'" size="48" class="mr-4">
                <v-icon :color="dashboard.lojas.atrasados > 0 ? 'white' : 'grey'" size="24">mdi-clock-alert</v-icon>
              </v-avatar>
              <div>
                <p class="text-h5 font-weight-bold mb-0">{{ dashboard.lojas.atrasados | formatNumber }}</p>
                <p class="text-caption grey--text mb-0">Mensalidades atrasadas</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="situation-card" :class="{ 'situation-danger': dashboard.lojas.bloqueadas > 0 }" elevation="2">
            <v-card-text class="d-flex align-center pa-4">
              <v-avatar :color="dashboard.lojas.bloqueadas > 0 ? 'error' : 'grey lighten-2'" size="48" class="mr-4">
                <v-icon :color="dashboard.lojas.bloqueadas > 0 ? 'white' : 'grey'" size="24">mdi-lock</v-icon>
              </v-avatar>
              <div>
                <p class="text-h5 font-weight-bold mb-0">{{ dashboard.lojas.bloqueadas | formatNumber }}</p>
                <p class="text-caption grey--text mb-0">Bloqueados</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="situation-card" elevation="2">
            <v-card-text class="d-flex align-center pa-4">
              <v-avatar color="blue-grey lighten-1" size="48" class="mr-4">
                <v-icon color="white" size="24">mdi-flask</v-icon>
              </v-avatar>
              <div>
                <p class="text-h5 font-weight-bold mb-0">{{ dashboard.lojas.demos | formatNumber }}</p>
                <p class="text-caption grey--text mb-0">Demonstração</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="situation-card" :class="{ 'situation-danger': dashboard.lojas.removidas > 0 }" elevation="2">
            <v-card-text class="d-flex align-center pa-4">
              <v-avatar :color="dashboard.lojas.removidas > 0 ? 'deep-orange' : 'grey lighten-2'" size="48" class="mr-4">
                <v-icon :color="dashboard.lojas.removidas > 0 ? 'white' : 'grey'" size="24">mdi-delete-clock</v-icon>
              </v-avatar>
              <div>
                <p class="text-h5 font-weight-bold mb-0">{{ dashboard.lojas.removidas | formatNumber }}</p>
                <p class="text-caption grey--text mb-0">Serão removidos</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Mapa de localização -->
      <v-row class="mb-6" v-if="lojasComCoordenadas.length > 0">
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="pb-0">
              <v-icon left color="primary">mdi-map-marker-multiple</v-icon>
              Localização dos estabelecimentos
            </v-card-title>
            <v-card-subtitle class="mt-1">
              {{ lojasComCoordenadas.length }} estabelecimentos com localização definida
            </v-card-subtitle>
            <v-card-text class="pa-0">
              <l-map ref="mapaLojas" :zoom="mapZoom" :center="mapCenter" :options="mapOptions"
                style="height: 400px; width: 100%; z-index: 0;">
                <l-tile-layer :url="tileUrl" :attribution="tileAttribution"></l-tile-layer>
                <l-circle-marker v-for="(loja, index) in lojasComCoordenadas" :key="index" :lat-lng="loja.coordenadas"
                  :radius="8" :color="getCorStatusLoja(loja)" :fill-color="getCorStatusLoja(loja)" :fill-opacity="0.7">
                  <l-tooltip>
                    <strong>{{ loja.nome }}</strong>
                  </l-tooltip>
                </l-circle-marker>
              </l-map>
            </v-card-text>
            <v-card-actions class="px-4 pb-3">
              <v-chip small class="mr-2" color="green" dark>
                <v-icon left x-small>mdi-circle</v-icon>
                Aberto
              </v-chip>
              <v-chip small class="mr-2" color="red" dark>
                <v-icon left x-small>mdi-circle</v-icon>
                Fechado
              </v-chip>
              <v-chip small color="grey" dark>
                <v-icon left x-small>mdi-circle</v-icon>
                Outros
              </v-chip>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { LMap, LTileLayer, LCircleMarker, LTooltip } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchApiClientes } from "../../services/api";
import { verificarSessao, encerrar, getMes } from "../../utils/utils";

export default {
  name: "Dashboard",
  components: {
    apexchart: VueApexCharts,
    LMap,
    LTileLayer,
    LCircleMarker,
    LTooltip
  },
  data() {
    return {
      data: new Date(),
      loading: false,
      error: {
        show: false,
        message: "",
      },
      modal: {},
      clientes: [],
      mapZoom: 4,
      mapCenter: [-15.7801, -47.9292],
      mapOptions: {
        zoomControl: true,
        attributionControl: false,
        scrollWheelZoom: false,
        dragging: true,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        touchZoom: true
      },
      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      tileAttribution: '',
      dashboard: {
        faturamento: {
          maior: [],
          menor: [],
        },
        vendas: {
          maior: [],
          menor: [],
        },
        criticas: [],
        lojas: {
          total: 0,
          abertas: 0,
          fechadas: 0,
          pausadas: 0,
          atrasados: 0,
          removidas: 0,
          bloqueadas: 0,
          demos: 0,
        },
        totalizadores: {
          faturamento: 0,
          pedidos: 0,
          usuarios: 0,
          visitas: 0,
        },
        infosRelevantes: {
          recorrenciaPedidos: 0,
          ticketMedioPedido: 0,
          avaliacoes: {
            qtd: 0,
            media: 0,
          },
        },
        estatisticas: {
          fiscal: {
            lojas30dias: 0,
            fiscal30dias: 0,
            lojas60dias: 0,
            fiscal60dias: 0,
            lojas90dias: 0,
            fiscal90dias: 0,
          },
          nfes: {
            lojas30dias: 0,
            nfes30dias: 0,
            lojas60dias: 0,
            nfes60dias: 0,
            lojas90dias: 0,
            nfes90dias: 0,
          },
          dfes: {
            lojas30dias: 0,
            dfes30dias: 0,
            lojas60dias: 0,
            dfes60dias: 0,
            lojas90dias: 0,
            dfes90dias: 0,
          },
          gamazap: {
            lojas30dias: 0,
            lojas60dias: 0,
            lojas90dias: 0,
          },
          gamazap_campanhas: {
            lojas30dias: 0,
            lojas60dias: 0,
            lojas90dias: 0,
          },
          robo: {
            lojas30dias: 0,
            lojas60dias: 0,
            lojas90dias: 0,
          },
          gtef: {
            lojas30dias: 0,
            lojas60dias: 0,
            lojas90dias: 0,
          },
          gpos: {
            lojas30dias: 0,
            lojas60dias: 0,
            lojas90dias: 0,
          },
          gmesas: {
            lojas30dias: 0,
            lojas60dias: 0,
            lojas90dias: 0,
          },
          gtotem: {
            lojas30dias: 0,
            lojas60dias: 0,
            lojas90dias: 0,
          },
          suporte: {
            lojas30dias: 0,
            lojas60dias: 0,
            lojas90dias: 0,
          },
          transacoes_online: {
            lojas30dias: 0,
            lojas60dias: 0,
            lojas90dias: 0,
          },
        },
      },
      token: localStorage.getItem("token"),
      responsavel: {},
    };
  },
  computed: {
    mesAtual() {
      return new Date(0, this.data.getMonth(), 1).toLocaleString("pt-BR", { month: "long" }).replace(/^\w/, c => c.toUpperCase());
    },
    lojasComCoordenadas() {
      return this.clientes
        .filter(loja => {
          const coords = loja.analytics?.endereco?.coordenadas;
          if (!coords || typeof coords !== 'string') return false;
          const partes = coords.split(',');
          if (partes.length !== 2) return false;
          const lat = parseFloat(partes[0]);
          const lng = parseFloat(partes[1]);
          return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
        })
        .map(loja => {
          const partes = loja.analytics.endereco.coordenadas.split(',');
          return {
            nome: loja.nome,
            status: loja.status,
            coordenadas: [parseFloat(partes[0]), parseFloat(partes[1])]
          };
        });
    },
    chartLojasSeries() {
      return [
        this.dashboard.lojas.abertas,
        this.dashboard.lojas.fechadas,
        this.dashboard.lojas.bloqueadas,
        this.dashboard.lojas.demos
      ];
    },
    chartLojasOptions() {
      return {
        chart: {
          type: 'donut',
          fontFamily: 'Roboto, sans-serif'
        },
        labels: ['Abertos', 'Fechados', 'Bloqueados', 'Demo'],
        colors: ['#4CAF50', '#F44336', '#FF9800', '#9E9E9E'],
        legend: {
          position: 'bottom'
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  formatter: () => this.dashboard.lojas.total
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => val.toFixed(0) + '%'
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: { width: 280 },
            legend: { position: 'bottom' }
          }
        }]
      };
    },
    chartFiscalSeries() {
      return [
        {
          name: 'NFC-e',
          data: [
            this.dashboard.estatisticas.fiscal.fiscal90dias,
            this.dashboard.estatisticas.fiscal.fiscal60dias,
            this.dashboard.estatisticas.fiscal.fiscal30dias
          ]
        },
        {
          name: 'NF-e',
          data: [
            this.dashboard.estatisticas.nfes.nfes90dias,
            this.dashboard.estatisticas.nfes.nfes60dias,
            this.dashboard.estatisticas.nfes.nfes30dias
          ]
        },
        {
          name: 'DF-e',
          data: [
            this.dashboard.estatisticas.dfes.dfes90dias,
            this.dashboard.estatisticas.dfes.dfes60dias,
            this.dashboard.estatisticas.dfes.dfes30dias
          ]
        }
      ];
    },
    chartFiscalOptions() {
      return {
        chart: {
          type: 'area',
          fontFamily: 'Roboto, sans-serif',
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        colors: ['#2196F3', '#4CAF50', '#FF9800'],
        stroke: {
          curve: 'smooth',
          width: 3
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.1,
            stops: [0, 90, 100]
          }
        },
        xaxis: {
          categories: [this.getMesNome(-2), this.getMesNome(-1), this.getMesNome(0)]
        },
        yaxis: {
          labels: {
            formatter: (val) => this.formatNumberShort(val)
          }
        },
        tooltip: {
          y: {
            formatter: (val) => val.toLocaleString('pt-BR') + ' emissões'
          }
        },
        legend: {
          position: 'top',
          horizontalAlign: 'right'
        },
        dataLabels: { enabled: false }
      };
    },
    chartTopFaturamentoSeries() {
      const top10 = this.dashboard.faturamento.maior.slice(0, 10);
      return [{
        name: 'Faturamento',
        data: top10.map(loja => parseFloat(loja.analytics?.fat30dias || 0))
      }];
    },
    chartTopFaturamentoOptions() {
      const top10 = this.dashboard.faturamento.maior.slice(0, 10);
      return {
        chart: {
          type: 'bar',
          fontFamily: 'Roboto, sans-serif',
          toolbar: { show: false }
        },
        colors: ['#4CAF50'],
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            dataLabels: { position: 'top' }
          }
        },
        xaxis: {
          categories: top10.map(loja => this.truncateText(loja.nome, 20)),
          labels: {
            formatter: (val) => this.formatCurrencyShort(val)
          }
        },
        yaxis: {
          labels: {
            style: { fontSize: '11px' }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => this.formatCurrency(val)
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => this.formatCurrencyShort(val),
          offsetX: 30,
          style: { fontSize: '11px', colors: ['#333'] }
        }
      };
    },
    chartTopVendasSeries() {
      const top10 = this.dashboard.vendas.maior.slice(0, 10);
      return [{
        name: 'Vendas',
        data: top10.map(loja => parseInt(loja.analytics?.totalPedidos || 0))
      }];
    },
    chartTopVendasOptions() {
      const top10 = this.dashboard.vendas.maior.slice(0, 10);
      return {
        chart: {
          type: 'bar',
          fontFamily: 'Roboto, sans-serif',
          toolbar: { show: false }
        },
        colors: ['#2196F3'],
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            dataLabels: { position: 'top' }
          }
        },
        xaxis: {
          categories: top10.map(loja => this.truncateText(loja.nome, 20)),
          labels: {
            formatter: (val) => this.formatNumberShort(val)
          }
        },
        yaxis: {
          labels: {
            style: { fontSize: '11px' }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => val.toLocaleString('pt-BR') + ' vendas'
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => val.toLocaleString('pt-BR'),
          offsetX: 30,
          style: { fontSize: '11px', colors: ['#333'] }
        }
      };
    },
    lojasAtivasMenorFaturamento() {
      return this.dashboard.faturamento.menor
        .filter(loja => loja.ativo !== false)
        .slice(0, 10);
    },
    chartMenorFaturamentoSeries() {
      return [{
        name: 'Faturamento',
        data: this.lojasAtivasMenorFaturamento.map(loja => parseFloat(loja.analytics?.fat30dias || 0))
      }];
    },
    chartMenorFaturamentoOptions() {
      return {
        chart: {
          type: 'bar',
          fontFamily: 'Roboto, sans-serif',
          toolbar: { show: false }
        },
        colors: ['#FF9800'],
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            dataLabels: { position: 'top' }
          }
        },
        xaxis: {
          categories: this.lojasAtivasMenorFaturamento.map(loja => this.truncateText(loja.nome, 25)),
          labels: {
            formatter: (val) => this.formatCurrencyShort(val)
          }
        },
        yaxis: {
          labels: {
            style: { fontSize: '11px' }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => this.formatCurrency(val)
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => this.formatCurrencyShort(val),
          offsetX: 30,
          style: { fontSize: '11px', colors: ['#333'] }
        }
      };
    },
    chartServicosSeries() {
      return [{
        name: 'Lojas',
        data: [
          this.dashboard.estatisticas.gamazap.lojas30dias,
          this.dashboard.estatisticas.gamazap_campanhas.lojas30dias,
          this.dashboard.estatisticas.robo.lojas30dias,
          this.dashboard.estatisticas.gtef.lojas30dias,
          this.dashboard.estatisticas.gpos.lojas30dias,
          this.dashboard.estatisticas.gmesas.lojas30dias,
          this.dashboard.estatisticas.gtotem.lojas30dias,
          this.dashboard.estatisticas.suporte.lojas30dias,
          this.dashboard.estatisticas.transacoes_online.lojas30dias,
        ]
      }];
    },
    chartServicosOptions() {
      return {
        chart: {
          type: 'bar',
          fontFamily: 'Roboto, sans-serif',
          toolbar: { show: false }
        },
        colors: ['#25D366', '#FF5722', '#00BCD4', '#3F51B5', '#9C27B0', '#FF9800', '#000000', '#8B4513'],
        plotOptions: {
          bar: {
            borderRadius: 8,
            columnWidth: '50%',
            distributed: true
          }
        },
        xaxis: {
          categories: ['WhatsApp', 'Marketing', 'Robô', 'G-TEF', 'G-POS', 'G-MESAS', 'G-TOTEM', 'Suporte']
        },
        yaxis: {
          labels: {
            formatter: (val) => val.toLocaleString('pt-BR')
          }
        },
        legend: { show: false },
        dataLabels: {
          enabled: true,
          formatter: (val) => val.toLocaleString('pt-BR'),
          style: { fontSize: '12px', fontWeight: 'bold' }
        }
      };
    },
    servicosComparativo() {
      const calcTendencia = (atual, anterior) => {
        if (anterior === 0) return atual > 0 ? 100 : 0;
        return Math.round(((atual - anterior) / anterior) * 100);
      };

      return [
        {
          nome: 'NFC-e emitidas',
          icone: 'mdi-file-document',
          cor: 'blue',
          mes1: this.dashboard.estatisticas.fiscal.fiscal30dias,
          mes2: this.dashboard.estatisticas.fiscal.fiscal60dias,
          mes3: this.dashboard.estatisticas.fiscal.fiscal90dias,
          tendencia: calcTendencia(this.dashboard.estatisticas.fiscal.fiscal30dias, this.dashboard.estatisticas.fiscal.fiscal60dias)
        },
        {
          nome: 'NF-e emitidas',
          icone: 'mdi-file-certificate',
          cor: 'green',
          mes1: this.dashboard.estatisticas.nfes.nfes30dias,
          mes2: this.dashboard.estatisticas.nfes.nfes60dias,
          mes3: this.dashboard.estatisticas.nfes.nfes90dias,
          tendencia: calcTendencia(this.dashboard.estatisticas.nfes.nfes30dias, this.dashboard.estatisticas.nfes.nfes60dias)
        },
        {
          nome: 'WhatsApp conectados',
          icone: 'mdi-whatsapp',
          cor: 'green darken-1',
          mes1: this.dashboard.estatisticas.gamazap.lojas30dias,
          mes2: this.dashboard.estatisticas.gamazap.lojas60dias,
          mes3: this.dashboard.estatisticas.gamazap.lojas90dias,
          tendencia: calcTendencia(this.dashboard.estatisticas.gamazap.lojas30dias, this.dashboard.estatisticas.gamazap.lojas60dias)
        },
        {
          nome: 'Marketing WhatsApp',
          icone: 'mdi-bullhorn',
          cor: 'deep-orange',
          mes1: this.dashboard.estatisticas.gamazap_campanhas.lojas30dias,
          mes2: this.dashboard.estatisticas.gamazap_campanhas.lojas60dias,
          mes3: this.dashboard.estatisticas.gamazap_campanhas.lojas90dias,
          tendencia: calcTendencia(this.dashboard.estatisticas.gamazap_campanhas.lojas30dias, this.dashboard.estatisticas.gamazap_campanhas.lojas60dias)
        },
        {
          nome: 'Robôs de atendimento',
          icone: 'mdi-robot',
          cor: 'cyan',
          mes1: this.dashboard.estatisticas.robo.lojas30dias,
          mes2: this.dashboard.estatisticas.robo.lojas60dias,
          mes3: this.dashboard.estatisticas.robo.lojas90dias,
          tendencia: calcTendencia(this.dashboard.estatisticas.robo.lojas30dias, this.dashboard.estatisticas.robo.lojas60dias)
        },
        {
          nome: 'G-TEF',
          icone: 'mdi-credit-card-wireless',
          cor: 'indigo',
          mes1: this.dashboard.estatisticas.gtef.lojas30dias,
          mes2: this.dashboard.estatisticas.gtef.lojas60dias,
          mes3: this.dashboard.estatisticas.gtef.lojas90dias,
          tendencia: calcTendencia(this.dashboard.estatisticas.gtef.lojas30dias, this.dashboard.estatisticas.gtef.lojas60dias)
        },
        {
          nome: 'G-POS',
          icone: 'mdi-cellphone-nfc',
          cor: 'purple',
          mes1: this.dashboard.estatisticas.gpos.lojas30dias,
          mes2: this.dashboard.estatisticas.gpos.lojas60dias,
          mes3: this.dashboard.estatisticas.gpos.lojas90dias,
          tendencia: calcTendencia(this.dashboard.estatisticas.gpos.lojas30dias, this.dashboard.estatisticas.gpos.lojas60dias)
        },
        {
          nome: 'G-MESAS',
          icone: 'mdi-tablet',
          cor: 'black',
          mes1: this.dashboard.estatisticas.gmesas.lojas30dias,
          mes2: this.dashboard.estatisticas.gmesas.lojas60dias,
          mes3: this.dashboard.estatisticas.gmesas.lojas90dias,
          tendencia: calcTendencia(this.dashboard.estatisticas.gmesas.lojas30dias, this.dashboard.estatisticas.gmesas.lojas60dias)
        },
        {
          nome: 'G-TOTEM',
          icone: 'mdi-laptop',
          cor: 'brown',
          mes1: this.dashboard.estatisticas.gtotem.lojas30dias,
          mes2: this.dashboard.estatisticas.gtotem.lojas60dias,
          mes3: this.dashboard.estatisticas.gtotem.lojas90dias,
          tendencia: calcTendencia(this.dashboard.estatisticas.gtotem.lojas30dias, this.dashboard.estatisticas.gtotem.lojas60dias)
        },
        {
          nome: 'Suporte dedicado',
          icone: 'mdi-headset',
          cor: 'orange',
          mes1: this.dashboard.estatisticas.suporte.lojas30dias,
          mes2: this.dashboard.estatisticas.suporte.lojas60dias,
          mes3: this.dashboard.estatisticas.suporte.lojas90dias,
          tendencia: calcTendencia(this.dashboard.estatisticas.suporte.lojas30dias, this.dashboard.estatisticas.suporte.lojas60dias)
        },
        {
          nome: 'Transações online',
          icone: 'mdi-credit-card-check-outline',
          cor: 'green',
          mes1: this.dashboard.estatisticas.transacoes_online.lojas30dias,
          mes2: this.dashboard.estatisticas.transacoes_online.lojas60dias,
          mes3: this.dashboard.estatisticas.transacoes_online.lojas90dias,
          tendencia: calcTendencia(this.dashboard.estatisticas.transacoes_online.lojas30dias, this.dashboard.estatisticas.transacoes_online.lojas60dias)
        },
      ];
    }
  },
  async created() {
    window.addEventListener("offline", () => (this.loading = true));
    window.addEventListener("online", () => (this.loading = false));
    verificarSessao(this.atualizar);
    setTimeout(verificarSessao, 1 * 60000);
  },
  methods: {
    getCorStatusLoja(loja) {
      if (loja.status === '🟢') return '#4CAF50';
      if (loja.status === '🔴' || loja.status === '🟠') return '#F44336';
      return '#9E9E9E';
    },
    formatCurrency(value) {
      return parseFloat(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatCurrencyShort(value) {
      if (value >= 1000000) return 'R$ ' + (value / 1000000).toFixed(1) + 'M';
      if (value >= 1000) return 'R$ ' + (value / 1000).toFixed(1) + 'K';
      return 'R$ ' + value.toFixed(0);
    },
    formatNumberShort(value) {
      if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
      if (value >= 1000) return (value / 1000).toFixed(1) + 'K';
      return value.toString();
    },
    truncateText(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
    getMesNome(offset = 0) {
      const date = new Date();
      date.setMonth(date.getMonth() + offset);
      return date.toLocaleString("pt-BR", { month: "short" }).replace('.', '').toUpperCase();
    },
    getTendenciaColor(tendencia) {
      if (tendencia > 0) return 'success';
      if (tendencia < 0) return 'error';
      return 'grey';
    },
    getTendenciaIcon(tendencia) {
      if (tendencia > 0) return 'mdi-trending-up';
      if (tendencia < 0) return 'mdi-trending-down';
      return 'mdi-minus';
    },
    getTendenciaLabel(tendencia) {
      if (tendencia > 0) return '+' + tendencia + '%';
      return tendencia + '%';
    },
    setLoading(loading) {
      this.loading = loading;
      this.$root.$emit('global-loading', loading);
    },
    setError(error, title) {
      console.error(title, error);
      this.error = {
        show: true,
        title: title ? title : "Ops, falhou!",
        message: error ? error : "Falha não identificada, tente novamente.",
      };
    },
    atualizar() {
      this.setLoading(true);
      fetchApiClientes(this.token)
        .then((response) => {
          this.setLoading(false);
          return this.montar(response.data);
        })
        .catch((error) => {
          this.setLoading(false);
          switch (error?.response?.status) {
            case 401:
              return this.sair();
            default:
              this.setError(error?.response?.data?.message);
              break;
          }
        });
    },
    montar(data) {
      try {
        this.clientes = data?.clientes || [];
        this.dashboard = {
          data: new Date(),
          faturamento: {
            maior: [],
            menor: [],
          },
          vendas: {
            maior: [],
            menor: [],
          },
          criticas: [],
          lojas: {
            total: 0,
            abertas: 0,
            fechadas: 0,
            pausadas: 0,
            atrasados: 0,
            removidas: data?.clientes?.filter(loja => loja.ativo === false).length,
            bloqueadas: 0,
            demos: 0,
          },
          totalizadores: {
            faturamento: 0,
            pedidos: 0,
            usuarios: 0,
            visitas: 0,
          },
          infosRelevantes: {
            recorrenciaPedidos: 0,
            ticketMedioPedido: 0,
            avaliacoes: {
              qtd: 0,
              media: 0,
            },
          },
          estatisticas: {
            fiscal: {
              lojas30dias: 0,
              fiscal30dias: 0,
              lojas60dias: 0,
              fiscal60dias: 0,
              lojas90dias: 0,
              fiscal90dias: 0,
            },
            nfes: {
              lojas30dias: 0,
              nfes30dias: 0,
              lojas60dias: 0,
              nfes60dias: 0,
              lojas90dias: 0,
              nfes90dias: 0,
            },
            dfes: {
              lojas30dias: 0,
              dfes30dias: 0,
              lojas60dias: 0,
              dfes60dias: 0,
              lojas90dias: 0,
              dfes90dias: 0,
            },
            gamazap: {
              lojas30dias: 0,
              lojas60dias: 0,
              lojas90dias: 0,
            },
            gamazap_campanhas: {
              lojas30dias: 0,
              lojas60dias: 0,
              lojas90dias: 0,
            },
            robo: {
              lojas30dias: 0,
              lojas60dias: 0,
              lojas90dias: 0,
            },
            gtef: {
              lojas30dias: 0,
              lojas60dias: 0,
              lojas90dias: 0,
            },
            gpos: {
              lojas30dias: 0,
              lojas60dias: 0,
              lojas90dias: 0,
            },
            gmesas: {
              lojas30dias: 0,
              lojas60dias: 0,
              lojas90dias: 0,
            },
            gtotem: {
              lojas30dias: 0,
              lojas60dias: 0,
              lojas90dias: 0,
            },
            suporte: {
              lojas30dias: 0,
              lojas60dias: 0,
              lojas90dias: 0,
            },
            transacoes_online: {
              lojas30dias: 0,
              lojas60dias: 0,
              lojas90dias: 0,
            },
          },
        };

        if (data?.responsavel) {
          this.responsavel = data?.responsavel;
        }

        let haveAnalytics = data?.clientes?.filter((loja) => {
          this.dashboard.lojas.total++;
          switch (loja.status) {
            case "🟢":
              this.dashboard.lojas.abertas++;
              break;
            case "🔴":
            case "🟠":
              this.dashboard.lojas.fechadas++;
              break;
          }

          if (loja.situacao != "🟢") {
            this.dashboard.lojas.atrasados++;
          }
          if (loja.block === "🔴") {
            this.dashboard.lojas.bloqueadas++;
          }
          if (/demo/gi.test(loja.responsavel)) {
            this.dashboard.lojas.demos++;
          }

          if (loja.manutencao == "🔴") {
            this.dashboard.criticas.push({
              critica: `${loja.nome} está com o sistema em manutenção.`,
            });
          }
          if (loja.logs == "🟢") {
            this.dashboard.criticas.push({
              critica: `${loja.nome} está com o log ativo.`,
            });
          }
          if (loja.cache == "🔴") {
            this.dashboard.criticas.push({
              critica: `${loja.nome} está com o cache desativado.`,
            });
          }

          if (loja.analytics) {
            this.dashboard.totalizadores.faturamento += parseFloat(
              loja.analytics?.fat30dias ?? 0
            );
            this.dashboard.totalizadores.pedidos += parseFloat(
              loja.analytics?.totalPedidos ?? 0
            );
            this.dashboard.totalizadores.usuarios += parseFloat(
              loja.analytics?.totalUsuarios ?? 0
            );
            this.dashboard.totalizadores.visitas += parseFloat(
              loja.analytics?.visitas30dias ?? 0
            );

            let rejeicoes = parseInt(
              loja.analytics?.fiscalRejeitadas30dias ?? 0
            );
            if (rejeicoes > 0) {
              this.dashboard.criticas.push({
                critica: `${loja.nome} está com ${rejeicoes} NFC-es rejeitadas que precisam de atenção.`,
              });
            }
            this.dashboard.estatisticas.fiscal.fiscal30dias += parseInt(
              loja.analytics?.fiscal30dias ?? 0
            );
            if (loja.analytics?.fiscal30dias > 0) {
              this.dashboard.estatisticas.fiscal.lojas30dias += 1;
            }
            this.dashboard.estatisticas.fiscal.fiscal60dias += parseInt(
              loja.analytics?.fiscal60dias ?? 0
            );
            if (loja.analytics?.fiscal60dias > 0) {
              this.dashboard.estatisticas.fiscal.lojas60dias += 1;
            }
            this.dashboard.estatisticas.fiscal.fiscal90dias += parseInt(
              loja.analytics?.fiscal90dias ?? 0
            );
            if (loja.analytics?.fiscal90dias > 0) {
              this.dashboard.estatisticas.fiscal.lojas90dias += 1;
            }

            this.dashboard.estatisticas.nfes.nfes30dias += parseInt(
              loja.analytics?.nfes30dias ?? 0
            );
            if (loja.analytics?.nfes30dias > 0) {
              this.dashboard.estatisticas.nfes.lojas30dias += 1;
            }
            this.dashboard.estatisticas.nfes.nfes60dias += parseInt(
              loja.analytics?.nfes60dias ?? 0
            );
            if (loja.analytics?.nfes60dias > 0) {
              this.dashboard.estatisticas.nfes.lojas60dias += 1;
            }
            this.dashboard.estatisticas.nfes.nfes90dias += parseInt(
              loja.analytics?.nfes90dias ?? 0
            );
            if (loja.analytics?.nfes90dias > 0) {
              this.dashboard.estatisticas.nfes.lojas90dias += 1;
            }

            this.dashboard.estatisticas.dfes.dfes30dias += parseInt(
              loja.analytics?.dfes30dias ?? 0
            );
            if (loja.analytics?.dfes30dias > 0) {
              this.dashboard.estatisticas.dfes.lojas30dias += 1;
            }
            this.dashboard.estatisticas.dfes.dfes60dias += parseInt(
              loja.analytics?.dfes60dias ?? 0
            );
            if (loja.analytics?.dfes60dias > 0) {
              this.dashboard.estatisticas.dfes.lojas60dias += 1;
            }
            this.dashboard.estatisticas.dfes.dfes90dias += parseInt(
              loja.analytics?.dfes90dias ?? 0
            );
            if (loja.analytics?.nfes90dias > 0) {
              this.dashboard.estatisticas.nfes.lojas90dias += 1;
            }

            if (loja.analytics[`modulos_mes${getMes()}`]) {
              this.dashboard.estatisticas.gamazap.lojas30dias += parseInt(
                loja.analytics[`modulos_mes${getMes()}`].gamazap ?? 0
              );

              this.dashboard.estatisticas.gamazap_campanhas.lojas30dias +=
                parseInt(
                  loja.analytics[`modulos_mes${getMes()}`].gamazap_campanhas ??
                  0
                );

              this.dashboard.estatisticas.robo.lojas30dias += parseInt(
                loja.analytics[`modulos_mes${getMes()}`].robo ?? 0
              );

              this.dashboard.estatisticas.gtef.lojas30dias += parseInt(
                loja.analytics[`modulos_mes${getMes()}`].gtef ?? 0
              );

              this.dashboard.estatisticas.gpos.lojas30dias += parseInt(
                loja.analytics[`modulos_mes${getMes()}`].gpos ?? 0
              );

              this.dashboard.estatisticas.gmesas.lojas30dias += parseInt(
                loja.analytics[`modulos_mes${getMes()}`].gmesas ?? 0
              );

              this.dashboard.estatisticas.gtotem.lojas30dias += parseInt(
                loja.analytics[`modulos_mes${getMes()}`].gtotem ?? 0
              );

              this.dashboard.estatisticas.suporte.lojas30dias += parseInt(
                loja.analytics[`modulos_mes${getMes()}`].suporte_dedicado ?? 0
              );

              this.dashboard.estatisticas.transacoes_online.lojas30dias += parseInt(
                loja.analytics[`modulos_mes${getMes()}`].transacoes_online ?? 0
              );
            }
            if (loja.analytics[`modulos_mes${getMes(-1)}`]) {
              this.dashboard.estatisticas.gamazap.lojas60dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-1)}`].gamazap ?? 0
              );

              this.dashboard.estatisticas.gamazap_campanhas.lojas60dias +=
                parseInt(
                  loja.analytics[`modulos_mes${getMes(-1)}`]
                    .gamazap_campanhas ?? 0
                );

              this.dashboard.estatisticas.robo.lojas60dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-1)}`].robo ?? 0
              );

              this.dashboard.estatisticas.gtef.lojas60dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-1)}`].gtef ?? 0
              );

              this.dashboard.estatisticas.gpos.lojas60dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-1)}`].gpos ?? 0
              );

              this.dashboard.estatisticas.gmesas.lojas60dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-1)}`].gmesas ?? 0
              );

              this.dashboard.estatisticas.gtotem.lojas60dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-1)}`].gtotem ?? 0
              );

              this.dashboard.estatisticas.suporte.lojas60dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-1)}`].suporte_dedicado ?? 0
              );

              this.dashboard.estatisticas.transacoes_online.lojas60dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-1)}`].transacoes_online ?? 0
              );
            }

            if (loja.analytics[`modulos_mes${getMes(-2)}`]) {
              this.dashboard.estatisticas.gamazap.lojas90dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-2)}`].gamazap ?? 0
              );

              this.dashboard.estatisticas.gamazap_campanhas.lojas90dias +=
                parseInt(
                  loja.analytics[`modulos_mes${getMes(-2)}`]
                    .gamazap_campanhas ?? 0
                );

              this.dashboard.estatisticas.robo.lojas90dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-2)}`].robo ?? 0
              );

              this.dashboard.estatisticas.gtef.lojas90dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-2)}`].gtef ?? 0
              );

              this.dashboard.estatisticas.gpos.lojas90dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-2)}`].gpos ?? 0
              );

              this.dashboard.estatisticas.gmesas.lojas90dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-2)}`].gmesas ?? 0
              );

              this.dashboard.estatisticas.gtotem.lojas90dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-2)}`].gtotem ?? 0
              );

              this.dashboard.estatisticas.suporte.lojas90dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-2)}`].suporte_dedicado ?? 0
              );

              this.dashboard.estatisticas.transacoes_online.lojas90dias += parseInt(
                loja.analytics[`modulos_mes${getMes(-2)}`].transacoes_online ?? 0
              );
            }

            this.dashboard.infosRelevantes.avaliacoes.qtd++;
            this.dashboard.infosRelevantes.avaliacoes.media += parseFloat(
              loja.analytics.mediaAvaliacao ?? 0
            );
          }

          return loja?.analytics?.fat30dias >= 0 ? true : false;
        });

        let topFaturamentos = [...haveAnalytics].sort(function (a, b) {
          return parseFloat(a.analytics.fat30dias) >
            parseFloat(b.analytics.fat30dias)
            ? -1
            : 1;
        });
        let piorFaturamento = [...haveAnalytics].sort(function (a, b) {
          return a.analytics &&
            parseFloat(a.analytics.fat30dias) >
            parseFloat(b.analytics.fat30dias)
            ? 1
            : -1;
        });
        this.dashboard.faturamento = {
          maior: topFaturamentos.slice(0, 20),
          menor: piorFaturamento.slice(0, 20),
        };

        let topVendas = [...haveAnalytics].sort(function (a, b) {
          return parseFloat(a.analytics.totalPedidos) >
            parseFloat(b.analytics.totalPedidos)
            ? -1
            : 1;
        });
        let piorVendas = [...haveAnalytics].sort(function (a, b) {
          return parseFloat(a.analytics.totalPedidos) >
            parseFloat(b.analytics.totalPedidos)
            ? 1
            : -1;
        });
        this.dashboard.vendas = {
          maior: topVendas.slice(0, 20),
          menor: piorVendas.slice(0, 20),
        };

        this.dashboard.infosRelevantes.ticketMedioPedido = parseFloat(
          this.dashboard.totalizadores.faturamento /
          this.dashboard.totalizadores.pedidos
        );
        this.dashboard.infosRelevantes.avaliacoes.media = parseFloat(
          this.dashboard.infosRelevantes.avaliacoes.media /
          this.dashboard.infosRelevantes.avaliacoes.qtd
        ).toFixed(2);
        this.dashboard.infosRelevantes.recorrenciaPedidos = parseFloat(
          this.dashboard.totalizadores.pedidos /
          this.dashboard.totalizadores.usuarios
        ).toFixed(2);
        this.dashboard.infosRelevantes.emissoesFiscais30dias =
          this.dashboard.totalizadores.emissoesFiscais;
      } catch (error) {
        console.error(error);
        this.setError(error?.message);
      }
    },
    sair() {
      return encerrar();
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  padding: 24px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.dashboard-header h1 {
  letter-spacing: -0.5px;
}

.kpi-card {
  border-radius: 16px !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

.kpi-visitas {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.kpi-faturamento {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
}

.kpi-usuarios {
  background: linear-gradient(135deg, #ee0979 0%, #ff6a00 100%);
}

.kpi-pedidos {
  background: linear-gradient(135deg, #2196F3 0%, #21CBF3 100%);
}

.stat-mini-card {
  border-radius: 12px !important;
  transition: transform 0.2s ease;
}

.stat-mini-card:hover {
  transform: translateY(-2px);
}

.situation-card {
  border-radius: 12px !important;
  border-left: 4px solid transparent;
  transition: all 0.2s ease;
}

.situation-warning {
  border-left-color: #FF9800;
}

.situation-danger {
  border-left-color: #F44336;
}

.v-card {
  border-radius: 12px !important;
}

.fill-height {
  height: 100%;
}
</style>
